import type { Case } from "@@/utilities/casing";
import { setCase } from "@@/utilities/casing";
import type { LanguageCode } from "./language-code";
import { languageCodes } from "./language-code";
import type {
    Translatable,
    TranslatableType,
    TranslatableValue,
} from "./translation";
import { isTranslatableValue, translatableValueFactory } from "./translation";

const appendTranslatable = (
    t1: TranslatableType,
    t2: TranslatableType,
    separator?: string,
    options?: {
        ignoreFallbackLanguage?: boolean;
    },
): TranslatableValue => {
    // If a translatable is a string
    // the language is assumed to be swedish
    const value = appendTranslatableValue(
        translatableValueFactory.fromTranslatable(t1),
        translatableValueFactory.fromTranslatable(t2),
        separator,
        options,
    );
    return value;
};

const addSuffixToTranslatable = <T extends NonNullable<Translatable>>(
    suffix: string,
    translatable: T,
): T => {
    if (typeof translatable === "string") {
        return (translatable + suffix) as T;
    }
    if (isTranslatableValue(translatable)) {
        return translatableValueFactory.from({
            sv: translatable.sv + suffix,
            en: translatable.en ? translatable.en + suffix : undefined,
            de: translatable.de ? translatable.de + suffix : undefined,
        }) as T;
    }
    return translatable;
};
const translatableHasPlaceholder = (
    translatable: Translatable,
    placeholder: string,
): boolean => {
    if (typeof translatable === "string") {
        return translatable.includes(placeholder);
    }
    if (isTranslatableValue(translatable)) {
        return Object.values(translatable).some(
            value => typeof value === "string" && value.includes(placeholder),
        );
    }
    return false;
};

const replacePlaceholderInTranslatable = (
    translatable: TranslatableType,
    placeholder: string,
    replacement: string,
): TranslatableValue | null => {
    const _translatableValue: TranslatableValue | null = isTranslatableValue(
        translatable,
    )
        ? translatable
        : translatableValueFactory.fromTranslatable(translatable);

    if (_translatableValue === null) return null;

    return translatableValueFactory.from({
        sv: _translatableValue.sv.replaceAll(placeholder, replacement),
        en: _translatableValue.en
            ? _translatableValue.en.replaceAll(placeholder, replacement)
            : undefined,
        de: _translatableValue.de
            ? _translatableValue.de.replaceAll(placeholder, replacement)
            : undefined,
    });
};

const regexReplaceInTranslatable = (
    translatable: TranslatableType,
    regex: RegExp,
    replacement: string,
): TranslatableValue | null => {
    if (translatable === null) return null;

    const _translatableValue: TranslatableValue | null = isTranslatableValue(
        translatable,
    )
        ? translatable
        : translatableValueFactory.fromTranslatable(translatable);

    if (_translatableValue === null) return null;

    return translatableValueFactory.from({
        sv: _translatableValue.sv.replace(regex, replacement),
        en: _translatableValue.en
            ? _translatableValue.en.replace(regex, replacement)
            : undefined,
        de: _translatableValue.de
            ? _translatableValue.de.replace(regex, replacement)
            : undefined,
    });
};

const joinTranslatables = (
    array: TranslatableType[],
    separator?: string,
    options?: {
        ignoreFallbackLanguage?: boolean;
    },
): TranslatableValue => {
    let result: TranslatableValue = {
        _type: "TRANSLATABLE_VALUE",
        sv: "",
    };

    for (let index = 0; index < array.length; index++) {
        const item = array[index];
        result = appendTranslatable(result, item, separator, options);
    }

    return result;
};

const changeCaseForTranslatable = <T extends TranslatableType>(
    translatable: T,
    caseFormat: Case | "upper" | "lower",
): T => {
    return processTextsOfTranslatable(translatable, text => {
        if (caseFormat === "upper") {
            return text.toUpperCase();
        }
        if (caseFormat === "lower") {
            return text.toLowerCase();
        }
        return setCase(caseFormat)(text);
    });
};
const processTextsOfTranslatable = <T extends TranslatableType>(
    translatable: T,
    processText: (text: string) => string,
): T => {
    const result: TranslatableValue = {
        _type: "TRANSLATABLE_VALUE",
        sv: "",
    };

    const current = translatableValueFactory.fromTranslatable(translatable);
    if (!current) return translatable;

    for (const language of languageCodes) {
        if (current[language]) {
            result[language] = processText(current[language]);
        }
    }

    return result as T;
};

type TranslatableKeyValues = { sv: string } & {
    [key in Exclude<LanguageCode, "sv">]?: string | null;
};
const appendTranslatableValue = (
    t1: TranslatableValue | undefined | null,
    t2: TranslatableValue | undefined | null,
    separator: string = "",
    options?: {
        ignoreFallbackLanguage?: boolean;
    },
): TranslatableValue => {
    const combined: TranslatableKeyValues = {
        sv: "",
    };

    // Find all keys available in the two translatables provided
    const keys = Array.from(
        new Set(
            [
                ...(t1 ? (Object.keys(t1) as LanguageCode[]) : []),
                ...(t2 ? (Object.keys(t2) as LanguageCode[]) : []),
            ].filter(code => languageCodes.includes(code)),
        ),
    );

    // For every key, merge them together, and add separator if needed
    // but if one of the keys don't have a value, don't add a separator
    for (const key of keys) {
        const t1Value = (() => {
            if (options?.ignoreFallbackLanguage) return t1?.[key];
            if (key === "sv") return t1?.[key];
            if (key === "en") return t1?.[key] || t1?.sv;
            return t1?.[key] || t1?.en || t1?.sv;
        })();
        const t2Value = (() => {
            if (options?.ignoreFallbackLanguage) return t2?.[key];
            if (key === "sv") return t2?.[key];
            if (key === "en") return t2?.[key] || t2?.sv;
            return t2?.[key] || t2?.en || t2?.sv;
        })();
        const t1HasValue = !!t1Value;
        const t2HasValue = !!t2Value;
        const bothHaveValue = t1HasValue && t2HasValue;
        const value = (<Key extends LanguageCode>(
            valueKey: Key,
        ): TranslatableKeyValues[Key] => {
            if (bothHaveValue) {
                const value = `${t1Value}${separator ?? ""}${t2Value}`;
                return value;
            }
            if (t1HasValue) return t1Value as TranslatableKeyValues[Key];
            if (t2HasValue) return t2Value as TranslatableKeyValues[Key];
            return (
                valueKey === "sv" ? "" : null
            ) as TranslatableKeyValues[Key];
        })(key);

        if (key === "sv") {
            combined.sv = value || "";
            continue;
        }
        combined[key] = value;
        continue;
    }

    const result = translatableValueFactory.from(combined);
    return result;
};

const isTranslatableEmpty = (translatable: TranslatableType): boolean => {
    if (typeof translatable === "string") {
        return translatable.trim().length === 0;
    }
    if (isTranslatableValue(translatable)) {
        const sv = translatable.sv.trim();
        const en = translatable.en?.trim();
        const de = translatable.de?.trim();
        return (
            sv.length === 0 &&
            (!en || en.length === 0) &&
            (!de || de.length === 0)
        );
    }
    return true;
};

const isTranslatableNotEmpty = (translatable: TranslatableType) =>
    !isTranslatableEmpty(translatable);

//Todo check naming of function and for immutability
const textUtils = {
    append: appendTranslatable,
    appendValue: appendTranslatableValue,
    addSuffix: addSuffixToTranslatable,
    changeCase: changeCaseForTranslatable,
    processText: processTextsOfTranslatable,
    join: joinTranslatables,
    regexReplace: regexReplaceInTranslatable,
    isEmpty: isTranslatableEmpty,
    isNotEmpty: isTranslatableNotEmpty,
};

export {
    addSuffixToTranslatable,
    appendTranslatable,
    appendTranslatableValue,
    changeCaseForTranslatable,
    joinTranslatables,
    processTextsOfTranslatable,
    regexReplaceInTranslatable,
    replacePlaceholderInTranslatable,
    textUtils,
    translatableHasPlaceholder,
};
